import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { useDispatch } from 'react-redux'
import {
    Copy,
    Pencil,
    Trash2,
    PlayCircle,
    FolderOpen,
    ImageOff,
    Check,
    X,
} from 'lucide-react'
import MainHeader from '../ReusableComponents/Header/MainHeader'
import DialogModal from '../ReusableComponents/DialogModal/DialogModal'
import PrgmSelection from '../ReusableComponents/PrgmSlider/PrgmSelection/PrgmSelection'
import { getConnectedDeviceNameWithVersion } from '../../hooks/useBrandConfig'
import {
    listAiProjects,
    openAiProjectDb,
    deleteAiProjectDb,
    duplicateAiProjectDb,
    renameAiProjectDb,
} from './aiProjectDb'
import { listExampleProjects, openExampleProject } from './aiExampleProjects'
import styles from './AiShowcase.module.css'

// Full-page gallery of the projects saved to IndexedDB. Each card opens, renames,
// duplicates or deletes a project. Opening restores it and returns to the studio.
const AiShowcase = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const [projects, setProjects] = useState(null)
    const [busy, setBusy] = useState('')
    const [renameId, setRenameId] = useState('')
    const [renameText, setRenameText] = useState('')
    const [confirmDelete, setConfirmDelete] = useState(null)
    // Curated "Get started" examples fetched from the public bucket. null while
    // loading, [] if unavailable (section is simply hidden then).
    const [examples, setExamples] = useState(null)
    const [exBusy, setExBusy] = useState('')

    const refresh = async () => {
        try {
            setProjects(await listAiProjects())
        } catch (e) {
            setProjects([])
        }
    }

    useEffect(() => {
        refresh()
        listExampleProjects()
            .then(setExamples)
            .catch(() => setExamples([]))
    }, [])

    // Open a starter example: import its bundle, save a fresh copy in Your
    // Projects, then jump into the studio (no full reload).
    const onOpenExample = async (ex) => {
        if (exBusy || busy) return
        const key = ex.slug || ex.name
        setExBusy(key)
        try {
            await openExampleProject(ex)
            sessionStorage.setItem('webAiMode', 'true')
            dispatch({ type: 'HYDRATE_APP_MODE_STORAGE' })
            history.push('/ai-code')
        } catch (e) {
            setExBusy('')
            await refresh()
        }
    }

    const goBack = () => {
        sessionStorage.setItem('slideDirection', true)
        history.push('/ai-project')
    }

    const onOpen = async (id) => {
        if (busy) return
        setBusy(id)
        try {
            await openAiProjectDb(id)
            sessionStorage.setItem('webAiMode', 'true')
            // Refresh the Redux trainMachine slice from the just-restored storage
            // so the imported models/classes/images appear WITHOUT a full page
            // reload (keeps navigation smooth, no flash/lag).
            dispatch({ type: 'HYDRATE_APP_MODE_STORAGE' })
            history.push('/ai-code')
        } catch (e) {
            setBusy('')
            await refresh()
        }
    }

    const onDuplicate = async (id) => {
        await duplicateAiProjectDb(id)
        await refresh()
    }

    // The video link is an external page (YouTube etc.) — open it in a new tab.
    // Add a scheme if the user pasted a bare "youtube.com/..." address.
    const onPlayVideo = (url) => {
        const u = /^https?:\/\//i.test(url) ? url : `https://${url}`
        window.open(u, '_blank', 'noopener,noreferrer')
    }

    const startRename = (p) => {
        setRenameId(p.id)
        setRenameText(p.name || '')
    }
    const commitRename = async () => {
        if (renameText.trim()) await renameAiProjectDb(renameId, renameText.trim())
        setRenameId('')
        setRenameText('')
        await refresh()
    }

    const onDelete = async () => {
        const id = confirmDelete?.id
        setConfirmDelete(null)
        if (!id) return
        await deleteAiProjectDb(id)
        await refresh()
    }

    const fmtDate = (t) => {
        if (!t) return ''
        try {
            return new Date(t).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            })
        } catch {
            return ''
        }
    }

    return (
        <div className={styles.page}>
            <DialogModal
                show={!!confirmDelete}
                text={`Delete "${confirmDelete?.name || ''}"? This cannot be undone.`}
                showCloseBtn={false}
                optionsToSelect={[
                    { text: 'DELETE', do: onDelete },
                    { text: 'CANCEL', do: () => setConfirmDelete(null) },
                ]}
            />
            <MainHeader
                title={getConnectedDeviceNameWithVersion()}
                titlePosition="left"
                isGoBack={true}
                goBack={goBack}
                showAccountSetting={false}
                helper={
                    <div
                        className="PS-S_slide"
                        style={{ left: '70%', transform: 'translate(-50%)' }}
                    >
                        <PrgmSelection />
                    </div>
                }
            />

            <div className={styles.inner}>
                {examples && examples.length > 0 && (
                    <div>
                        <h1 className={styles.heading}>
                            Get started with these
                        </h1>
                        <div className={styles.grid}>
                            {examples.map((ex) => {
                                const key = ex.slug || ex.name
                                return (
                                    <div key={key} className={styles.card}>
                                        <div className={styles.thumb}>
                                            {ex.thumbnail ? (
                                                <img
                                                    src={ex.thumbnail}
                                                    alt={ex.name}
                                                    className={styles.thumbImg}
                                                />
                                            ) : (
                                                <ImageOff
                                                    size={40}
                                                    className={
                                                        styles.thumbPlaceholder
                                                    }
                                                />
                                            )}
                                            {ex.videoLink && (
                                                <span
                                                    className={styles.videoBadge}
                                                    title="Has a video"
                                                >
                                                    <PlayCircle size={16} />
                                                </span>
                                            )}
                                            <div className={styles.thumbOverlay}>
                                                <button
                                                    type="button"
                                                    className={styles.overlayBtn}
                                                    onClick={() =>
                                                        onOpenExample(ex)
                                                    }
                                                >
                                                    <FolderOpen size={26} />
                                                    <span>
                                                        {exBusy === key
                                                            ? 'Opening…'
                                                            : 'Open'}
                                                    </span>
                                                </button>
                                                {ex.videoLink && (
                                                    <button
                                                        type="button"
                                                        className={
                                                            styles.overlayBtn
                                                        }
                                                        onClick={() =>
                                                            onPlayVideo(
                                                                ex.videoLink
                                                            )
                                                        }
                                                    >
                                                        <PlayCircle size={26} />
                                                        <span>Video</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className={styles.info}>
                                            <p
                                                className={styles.name}
                                                title={ex.name}
                                            >
                                                {ex.name}
                                            </p>
                                            {ex.description && (
                                                <p className={styles.desc}>
                                                    {ex.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                <div>
                    <h1 className={styles.heading}>Your Projects</h1>

                    {projects === null ? (
                        <p className={styles.empty}>Loading…</p>
                    ) : projects.length === 0 ? (
                        <p className={styles.empty}>
                            No saved projects yet. Build something and press Save.
                        </p>
                    ) : (
                        <div className={styles.grid}>
                            {projects.map((p) => (
                                <div key={p.id} className={styles.card}>
                                    <div className={styles.thumb}>
                                        {p.thumbnail ? (
                                            <img
                                                src={p.thumbnail}
                                                alt={p.name}
                                                className={styles.thumbImg}
                                            />
                                        ) : (
                                            <ImageOff
                                                size={40}
                                                className={styles.thumbPlaceholder}
                                            />
                                        )}
                                        {p.videoLink && (
                                            <span
                                                className={styles.videoBadge}
                                                title="Has a video"
                                            >
                                                <PlayCircle size={16} />
                                            </span>
                                        )}
                                        <div className={styles.thumbOverlay}>
                                            <button
                                                type="button"
                                                className={styles.overlayBtn}
                                                onClick={() => onOpen(p.id)}
                                            >
                                                <FolderOpen size={26} />
                                                <span>
                                                    {busy === p.id
                                                        ? 'Opening…'
                                                        : 'Open'}
                                                </span>
                                            </button>
                                            {p.videoLink && (
                                                <button
                                                    type="button"
                                                    className={styles.overlayBtn}
                                                    onClick={() =>
                                                        onPlayVideo(p.videoLink)
                                                    }
                                                >
                                                    <PlayCircle size={26} />
                                                    <span>Video</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.info}>
                                        {renameId === p.id ? (
                                            <div className={styles.renameRow}>
                                                <input
                                                    className={styles.renameInput}
                                                    value={renameText}
                                                    autoFocus
                                                    onChange={(e) =>
                                                        setRenameText(e.target.value)
                                                    }
                                                    onKeyDown={(e) =>
                                                        e.key === 'Enter' &&
                                                        commitRename()
                                                    }
                                                />
                                                <button
                                                    className={styles.iconBtn}
                                                    onClick={commitRename}
                                                    aria-label="Save name"
                                                >
                                                    <Check size={16} />
                                                </button>
                                                <button
                                                    className={styles.iconBtn}
                                                    onClick={() => setRenameId('')}
                                                    aria-label="Cancel"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className={styles.name} title={p.name}>
                                                {p.name}
                                            </p>
                                        )}
                                        {p.description && (
                                            <p className={styles.desc}>
                                                {p.description}
                                            </p>
                                        )}
                                        <p className={styles.date}>
                                            {fmtDate(p.updatedAt)}
                                        </p>

                                        <div className={styles.actions}>
                                            <button
                                                className={styles.actionBtn}
                                                onClick={() => startRename(p)}
                                                title="Rename"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                className={styles.actionBtn}
                                                onClick={() => onDuplicate(p.id)}
                                                title="Duplicate"
                                            >
                                                <Copy size={16} />
                                            </button>
                                            <button
                                                className={`${styles.actionBtn} ${styles.danger}`}
                                                onClick={() =>
                                                    setConfirmDelete(p)
                                                }
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AiShowcase
